import React, { useState } from 'react';
import {
  TrendingUp,
  Users,
  MessageCircle,
  Heart,
  Clock,
  Calendar,
  ChevronDown
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// ── Mock data ─────────────────────────────────────────────────────────────────
const generateDailyData = (days) =>
  Array.from({ length: days }, (_, i) => ({
    date: new Date(Date.now() - (days - 1 - i) * 86400000).toLocaleDateString('en', {
      month: 'short',
      day: 'numeric'
    }),
    dau: Math.round(38000 + Math.random() * 12000),
    posts: Math.round(4200 + Math.random() * 2800),
    likes: Math.round(18000 + Math.random() * 9000)
  }));

const contentBreakdown = [
  { name: 'Text', value: 42, color: '#a78bfa' },
  { name: 'Photo', value: 35, color: '#38bdf8' },
  { name: 'Video', value: 23, color: '#f59e0b' }
];

const hourlyData = Array.from({ length: 24 }, (_, h) => ({
  hour: `${h}:00`,
  posts: Math.round(50 + Math.sin((h - 6) * 0.4) * 45 + Math.random() * 20)
}));

const topPosts = [
  { id: 1, author: 'Alex Johnson', preview: 'Just finished an amazing hike…', likes: 842, comments: 67 },
  { id: 2, author: 'Sarah Chen', preview: 'Launched my new portfolio website 🚀', likes: 731, comments: 89 },
  { id: 3, author: 'Michael Rodriguez', preview: 'Golden Gate sunset shots from today', likes: 619, comments: 42 },
  { id: 4, author: 'Emily Foster', preview: 'Coffee brewing session ☕ #ethiopia', likes: 502, comments: 28 },
  { id: 5, author: 'David Kim', preview: 'Weekend hiking trail — Marin County', likes: 478, comments: 35 }
];

// ── Sub-components ─────────────────────────────────────────────────────────────
const KPICard = ({ title, value, change, icon: Icon, color, delay = 0 }) => (
  <div
    className="glass-panel admin-card p-6 rounded-3xl"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-center justify-between mb-4">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${color}33, ${color}11)`, border: `1px solid ${color}44` }}
      >
        <Icon size={22} style={{ color }} />
      </div>
      <span
        className="text-xs font-bold px-2 py-0.5 rounded-full"
        style={{
          background: change >= 0 ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
          color: change >= 0 ? '#10b981' : '#ef4444'
        }}
      >
        {change >= 0 ? '+' : ''}{change}%
      </span>
    </div>
    <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-1">{title}</p>
    <p className="text-3xl font-bold text-white stat-glow">{value}</p>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="glass-panel admin-card p-6 rounded-3xl">
    <h3 className="text-lg font-bold text-white mb-6">{title}</h3>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel px-3 py-2 rounded-xl text-xs">
      <p className="text-white/60 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-bold">
          {p.name}: {p.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const Analytics = () => {
  const [range, setRange] = useState('30d');
  const data = generateDailyData(range === '7d' ? 7 : range === '30d' ? 30 : 90);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text flex items-center gap-3">
            <TrendingUp size={32} className="text-violet-400" />
            Analytics
          </h1>
          <p className="text-white/40 mt-1 text-sm">Platform performance & engagement overview.</p>
        </div>

        {/* Date range picker */}
        <div className="flex items-center gap-2 glass-panel px-4 py-2 rounded-xl w-fit">
          <Calendar size={16} className="text-white/40" />
          {['7d', '30d', '90d'].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                range === r
                  ? 'bg-violet-500/30 text-violet-300 border border-violet-500/40'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Daily Active Users" value="42.8k" change={8.2} icon={Users} color="#a78bfa" delay={0} />
        <KPICard title="Posts / Day" value="6.1k" change={3.5} icon={MessageCircle} color="#38bdf8" delay={60} />
        <KPICard title="Engagement Rate" value="14.7%" change={1.2} icon={Heart} color="#f43f5e" delay={120} />
        <KPICard title="Avg. Session" value="8m 24s" change={-2.1} icon={Clock} color="#f59e0b" delay={180} />
      </div>

      {/* Growth + Content breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="User & Post Growth">
          <div className="lg:col-span-2 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="dauGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#a78bfa" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="postsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#ffffff44', fontSize: 10 }} interval={Math.floor(data.length / 5)} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#ffffff44', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="dau" name="DAU" stroke="#a78bfa" strokeWidth={2} fill="url(#dauGrad)" />
                <Area type="monotone" dataKey="posts" name="Posts" stroke="#38bdf8" strokeWidth={2} fill="url(#postsGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Content Breakdown">
          <div className="h-[260px] flex flex-col items-center justify-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={contentBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {contentBreakdown.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex gap-4 mt-2">
              {contentBreakdown.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{ background: item.color }} />
                  <span className="text-white/50">{item.name}</span>
                  <span className="text-white font-bold">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Posts by hour */}
      <SectionCard title="Posts by Hour (UTC)">
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hourlyData} barSize={8}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fill: '#ffffff44', fontSize: 10 }} interval={3} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#ffffff44', fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="posts" name="Posts" radius={[4, 4, 0, 0]}>
                {hourlyData.map((_, i) => (
                  <Cell key={i} fill={`rgba(167,139,250,${0.3 + 0.5 * (_.posts / 120)})`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      {/* Top posts */}
      <SectionCard title="Top Posts This Period">
        <div className="space-y-3">
          {topPosts.map((post, i) => (
            <div
              key={post.id}
              className="flex items-center gap-4 p-3 rounded-2xl hover:bg-white/5 transition-colors cursor-pointer group"
            >
              <span className="text-2xl font-black text-white/10 group-hover:text-violet-400/40 transition-colors w-8">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{post.author}</p>
                <p className="text-xs text-white/40 truncate">{post.preview}</p>
              </div>
              <div className="flex items-center gap-3 text-xs text-white/40">
                <span className="flex items-center gap-1">
                  <Heart size={12} className="text-rose-400" />
                  {post.likes.toLocaleString()}
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle size={12} className="text-sky-400" />
                  {post.comments}
                </span>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

export default Analytics;
